/**
 * TilePersonality
 * -------------------------------------------------------------------------
 * Small animated character that lives INSIDE the existing ChoiceTile icon
 * chip (40×40 mobile, 48×48 sm+) — no tile size change. Adds "let go and
 * have fun" personality without changing the home-grid layout at all.
 *
 * Two states driven by the `alive` prop:
 *   • alive === false: greyed silhouette, gentle sway, tiny `z` bubble
 *   • alive === true:  brand-coloured character with a signature move
 *                      (envelope flap flip, trophy sparkles, chart bar
 *                       wave, calendar page flip, etc.)
 *
 * All CSS lives in index.css under the `.fb-pers-*` prefix. Every state
 * respects prefers-reduced-motion (falls back to static coloured/greyed).
 *
 * Props:
 *   kind    string   one of: envelope · clipboard · trophy · chart ·
 *                    people · podium · calendar · buildings · clock
 *   alive   boolean  true = active/celebratory, false = sleepy/bored
 *   testId  string   applied to root wrapper for tests
 */
import React from "react";

// Each personality is a tiny SVG-ish stack rendered inside a 40×40 box.
// Kept lightweight — mostly <span> boxes styled by CSS — so we can ship
// ~9 personalities without ballooning bundle size or hurting paint perf.

const PERSONALITIES = {
  envelope: () => (
    <div className="fb-pers-envelope">
      <span className="fb-pers-env-body" />
      <span className="fb-pers-env-flap" />
      <span className="fb-pers-env-face" />
    </div>
  ),
  clipboard: () => (
    <div className="fb-pers-clipboard">
      <span className="fb-pers-clip-clip" />
      <span className="fb-pers-clip-line" />
      <span className="fb-pers-clip-line" />
      <span className="fb-pers-clip-line last" />
    </div>
  ),
  trophy: () => (
    <div className="fb-pers-trophy">
      <span className="fb-pers-trophy-handle left" />
      <span className="fb-pers-trophy-handle right" />
      <span className="fb-pers-trophy-cup" />
      <span className="fb-pers-trophy-stem" />
      <span className="fb-pers-trophy-base" />
      <span className="fb-pers-sparkle s1">✦</span>
      <span className="fb-pers-sparkle s2">✦</span>
      <span className="fb-pers-sparkle s3">✦</span>
    </div>
  ),
  chart: () => (
    <div className="fb-pers-chart">
      <span /><span /><span /><span /><span />
      <span className="fb-pers-arrow">↗</span>
    </div>
  ),
  people: () => (
    <div className="fb-pers-people">
      <span className="fb-pers-person p1" />
      <span className="fb-pers-person p2" />
      <span className="fb-pers-person p3" />
    </div>
  ),
  podium: () => (
    <div className="fb-pers-podium">
      <span className="fb-pers-podium-block silver" />
      <span className="fb-pers-podium-block gold" />
      <span className="fb-pers-podium-block bronze" />
    </div>
  ),
  calendar: () => (
    <div className="fb-pers-calendar">
      <span className="fb-pers-cal-header" />
      <span className="fb-pers-cal-page" />
      <span className="fb-pers-cal-date">15</span>
    </div>
  ),
  buildings: () => (
    <div className="fb-pers-buildings">
      <span className="fb-pers-bldg b1">
        <span className="fb-pers-win" />
        <span className="fb-pers-win" />
      </span>
      <span className="fb-pers-bldg b2">
        <span className="fb-pers-win" />
        <span className="fb-pers-win" />
        <span className="fb-pers-win" />
      </span>
      <span className="fb-pers-bldg b3">
        <span className="fb-pers-win" />
        <span className="fb-pers-win" />
      </span>
    </div>
  ),
  clock: () => (
    <div className="fb-pers-clock">
      <span className="fb-pers-clock-face" />
      <span className="fb-pers-clock-hand hr" />
      <span className="fb-pers-clock-hand min" />
      <span className="fb-pers-clock-dot" />
    </div>
  ),
  // 2026-02 — Penny Rounding tile. Three stacked coins that gently
  // spin when alive (matching the "shiny copper" vibe of the physical
  // pennies the store is conserving). Bored state = greyed silhouette
  // with the standard `z` bubble like every other personality.
  coins: () => (
    <div className="fb-pers-coins">
      <span className="fb-pers-coin c1">¢</span>
      <span className="fb-pers-coin c2">¢</span>
      <span className="fb-pers-coin c3">¢</span>
    </div>
  ),
};

export default function TilePersonality({ kind, alive = false, testId }) {
  const Render = PERSONALITIES[kind];
  if (!Render) return null;
  return (
    <div
      className={`fb-pers-root ${alive ? "fb-pers-alive" : "fb-pers-bored"}`}
      data-testid={testId}
      data-state={alive ? "alive" : "bored"}
      aria-hidden="true"
    >
      <Render />
      {!alive && <span className="fb-pers-zzz">z</span>}
    </div>
  );
}
